import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default class HeaderHandler {
	init() {
		function headerHeight() {
			const header = document.querySelector('header');
			if (header) {
				const height = header.offsetHeight;
				document.documentElement.style.setProperty('--header', `${height}px`);
			}
		}
		headerHeight();
		window.addEventListener("resize", () => {
			headerHeight();
		});

		this.initMobileMenu();
		this.updateActiveLinks();
		this.initDropdownHandler();
		this.initHeaderTheme();
		this.headerAnimateIn();
	}
	
	scroll() {
		const header = document.querySelector('header');
		if (!header) return;

		const updateScrollTrigger = () => {
			// Kill any existing scroll triggers
			ScrollTrigger.getAll().forEach(trigger => {
				if (trigger.vars.id === 'headerScroll') {
					trigger.kill();
				}
			});

			// Get fresh header height on each update
			const headerHeight = header.offsetHeight;

			ScrollTrigger.create({
				id: 'headerScroll',
				start: 'top top',
				end: 'max',
				onUpdate: () => {
					const currentScrollY = window.scrollY;

					// After scrolling past header height, add data attribute
					if (currentScrollY > headerHeight) {
						header.setAttribute('data-header-scrolled', 'true');
					} else {
						header.removeAttribute('data-header-scrolled');
					}
				}
			});
		};
		updateScrollTrigger();
		window.addEventListener('resize', updateScrollTrigger);
	}
	
	reset() {
		const header = document.querySelector('header');
		if (header) {
			header.style.transform = '';
		}

		// Close mobile menu if open
		const hamburgerBtn = document.querySelector('[data-mobile-toggle]');
		const mobileMenu = document.querySelector('[data-mobile-menu]');
		const mobileOverlay = document.querySelector('[data-mobile-overlay]');

		if (hamburgerBtn && mobileMenu && mobileOverlay) {
			hamburgerBtn.setAttribute('aria-expanded', 'false');
			mobileMenu.classList.add('max-md:translate-x-full');
			mobileOverlay.classList.add('opacity-0', 'pointer-events-none');
			document.body.classList.remove('overflow-hidden');
		}

		// Close all submenus
		const allSubmenus = document.querySelectorAll('[data-mobile-submenu]');
		const allToggles = document.querySelectorAll('[data-submenu-toggle]');

		allSubmenus.forEach(submenu => {
			submenu.classList.add('max-md:translate-x-full');
			submenu.style.removeProperty('max-height');
		});

		allToggles.forEach(toggle => {
			toggle.setAttribute('aria-expanded', 'false');
		});

		// Reset dropdown state
		if (header) {
			header.setAttribute('data-dropdown-active', 'false');
		}

		// Force close desktop dropdowns by temporarily disabling pointer events
		// This prevents hover states from keeping dropdowns open during transitions
		const menuItems = document.querySelectorAll('nav > ul > li');
		menuItems.forEach(item => {
			item.classList.add('pointer-events-none');
		});

		// Re-enable pointer events after a short delay
		setTimeout(() => {
			menuItems.forEach(item => {
				item.classList.remove('pointer-events-none');
			});
		}, 500);
	}

	initMobileMenu() {
		const header = document.querySelector('header');
		const hamburgerBtn = document.querySelector('[data-mobile-toggle]');
		const mobileMenu = document.querySelector('[data-mobile-menu]');
		const mobileOverlay = document.querySelector('[data-mobile-overlay]');
		const mobileCloseBtn = document.querySelector('[data-mobile-close]');
		const submenuToggles = document.querySelectorAll('[data-submenu-toggle]');
		const menuLinks = document.querySelectorAll('[data-mobile-menu] a');

		if (!hamburgerBtn || !mobileMenu || !mobileOverlay) return;

		// Store original theme when menu is closed
		let originalTheme = null;

		// Function to close all submenus
		const closeAllSubmenus = (immediate = false) => {
			const allSubmenus = document.querySelectorAll('[data-mobile-submenu]');
			const allToggles = document.querySelectorAll('[data-submenu-toggle]');
			const isMobile = window.innerWidth < 1024;
			
			if (immediate) {
				// Immediate close without animation
				allSubmenus.forEach(submenu => {
					submenu.classList.add('max-md:translate-x-full');
					if (!isMobile) {
						submenu.style.maxHeight = '0px';
					} else {
						submenu.style.removeProperty('max-height');
					}
				});
			} else if (isMobile) {
				// For mobile, close submenus first, then main menu
				const openSubmenus = document.querySelectorAll('[data-mobile-submenu]:not(.max-md\\:translate-x-full)');
				let hasOpenSubmenus = openSubmenus.length > 0;
				
				openSubmenus.forEach((submenu, index) => {
					setTimeout(() => {
						submenu.classList.add('max-md:translate-x-full');
					}, index * 50); // Stagger the closing
				});
				
				// Return whether we had open submenus
				return hasOpenSubmenus;
			} else {
				// Desktop behavior
				allSubmenus.forEach(submenu => {
					submenu.style.maxHeight = '0px';
				});
			}
			
			allToggles.forEach(toggle => {
				toggle.setAttribute('aria-expanded', 'false');
			});
			
			return false;
		};

		// Toggle main mobile menu
		hamburgerBtn.addEventListener('click', () => {
			const isExpanded = hamburgerBtn.getAttribute('aria-expanded') === 'true';
			const isMobile = window.innerWidth < 1024;
			
			if (isExpanded) {
				// Closing menu
				if (isMobile) {
					// Check if submenus are open
					const hasOpenSubmenus = closeAllSubmenus();

					// Delay main menu close if submenus were open
					setTimeout(() => {
						hamburgerBtn.setAttribute('aria-expanded', 'false');
						mobileMenu.classList.add('max-md:translate-x-full');
						mobileOverlay.classList.add('opacity-0', 'pointer-events-none');
						document.body.classList.remove('overflow-hidden');

						// Restore original header theme
						if (header && originalTheme) {
							header.setAttribute('data-header-theme', originalTheme);
							originalTheme = null;
						}
					}, hasOpenSubmenus ? 200 : 0);
				} else {
					// Desktop - immediate close
					hamburgerBtn.setAttribute('aria-expanded', 'false');
					mobileMenu.classList.add('max-md:translate-x-full');
					mobileOverlay.classList.add('opacity-0', 'pointer-events-none');
					document.body.classList.remove('overflow-hidden');
					closeAllSubmenus();

					// Restore original header theme
					if (header && originalTheme) {
						header.setAttribute('data-header-theme', originalTheme);
						originalTheme = null;
					}
				}
			} else {
				// Opening menu
				hamburgerBtn.setAttribute('aria-expanded', 'true');
				mobileMenu.classList.remove('max-md:translate-x-full');
				mobileOverlay.classList.remove('opacity-0', 'pointer-events-none');
				document.body.classList.add('overflow-hidden');

				// Store current theme and set to light when menu opens (mobile only)
				if (isMobile && header) {
					originalTheme = header.getAttribute('data-header-theme');
					header.setAttribute('data-header-theme', 'light');
				}
				
				// Ensure all submenus are properly closed when opening menu
				if (isMobile) {
					closeAllSubmenus(true); // Immediate close without animation
				}
			}
		});

		// Close menu when clicking overlay
		mobileOverlay.addEventListener('click', () => {
			hamburgerBtn.click();
		});

		// Close menu when clicking close button
		if (mobileCloseBtn) {
			mobileCloseBtn.addEventListener('click', () => {
				hamburgerBtn.click();
			});
		}

		// Handle submenu toggles
		submenuToggles.forEach(btn => {
			btn.addEventListener('click', () => {
				const targetId = btn.getAttribute('data-submenu-toggle');
				const submenu = document.querySelector(`[data-mobile-submenu="${targetId}"]`);
				const isExpanded = btn.getAttribute('aria-expanded') === 'true';
				const isMobile = window.innerWidth < 1024;

				if (submenu) {
					btn.setAttribute('aria-expanded', !isExpanded);
					
					if (isMobile) {
						// Mobile behavior only
						if (isExpanded) {
							submenu.classList.add('max-md:translate-x-full');
						} else {
							submenu.classList.remove('max-md:translate-x-full');
						}
					} else {
						// Desktop behavior only
						if (isExpanded) {
							submenu.style.maxHeight = '0px';
						} else {
							submenu.style.maxHeight = submenu.scrollHeight + 'px';
						}
					}
				}
			});
		});

		// Handle submenu back buttons
		const submenuBackBtns = document.querySelectorAll('[data-submenu-back]');
		submenuBackBtns.forEach(btn => {
			btn.addEventListener('click', () => {
				const targetId = btn.getAttribute('data-submenu-back');
				const submenu = document.querySelector(`[data-mobile-submenu="${targetId}"]`);
				const toggle = document.querySelector(`[data-submenu-toggle="${targetId}"]`);
				
				if (submenu && toggle) {
					// Close submenu
					submenu.classList.add('max-md:translate-x-full');
					toggle.setAttribute('aria-expanded', 'false');
				}
			});
		});

		// Handle submenu close buttons
		const submenuCloseBtns = document.querySelectorAll('[data-mobile-submenu-close]');
		submenuCloseBtns.forEach(btn => {
			btn.addEventListener('click', () => {
				// Close the entire menu
				hamburgerBtn.click();
			});
		});

		// Close menu when clicking on a link
		menuLinks.forEach(link => {
			link.addEventListener('click', (e) => {
				// Check if this is an internal link (not external)
				const href = link.getAttribute('href');
				const isInternalLink = href && (href.startsWith('/') || href.startsWith(window.location.origin));
				const isMobile = window.innerWidth < 1024;
				
				if (isInternalLink && isMobile) {
					// For mobile, trigger the hamburger click which handles sequencing
					const isExpanded = hamburgerBtn.getAttribute('aria-expanded') === 'true';
					if (isExpanded) {
						hamburgerBtn.click();
					}
				}
			});
		});
	}

	updateActiveLinks() {
		const header = document.querySelector('header');
		if (!header) return;

		// Get all navigation links (both desktop and mobile)
		const navLinks = header.querySelectorAll('nav a');
		const navArrows = header.querySelectorAll('nav span svg');
		const currentUrl = location.href;

		// Remove active attribute from all links
		navLinks.forEach(link => {
			link.removeAttribute('data-active');
		});
		navArrows.forEach(arrow => {
			arrow.closest('span')?.removeAttribute('data-active');
		});

		// Find and mark active links
		navLinks.forEach(link => {
			const linkUrl = link.href;

			// Check for exact match or if current URL starts with link URL (for parent pages)
			if (linkUrl === currentUrl ||
				(linkUrl !== location.origin + '/' && currentUrl.startsWith(linkUrl) && linkUrl.endsWith('/'))) {

				// Add active data attribute to link
				link.setAttribute('data-active', 'true');

				// Find and mark associated arrow (next sibling span with SVG)
				const parentLi = link.closest('li');
				if (parentLi) {
					const arrowSpan = parentLi.querySelector('span');
					if (arrowSpan) {
						arrowSpan.setAttribute('data-active', 'true');
					}
				}
			}
		});
	}

	initDropdownHandler() {
		const header = document.querySelector('header');
		if (!header) return;

		// Desktop: Listen for hover on menu items with actual dropdowns
		const menuItems = header.querySelectorAll('nav > ul > li');

		menuItems.forEach(item => {
			// Check if this item actually has a dropdown/submenu
			const hasDropdown = item.querySelector('[data-mobile-submenu]');

			if (hasDropdown) {
				item.addEventListener('mouseenter', () => {
					if (window.innerWidth >= 1024) {
						header.setAttribute('data-dropdown-active', 'true');
					}
				});

				item.addEventListener('mouseleave', () => {
					if (window.innerWidth >= 1024) {
						header.setAttribute('data-dropdown-active', 'false');
					}
				});
			}
		});

		// Mobile: Listen for submenu toggle clicks
		const submenuToggles = document.querySelectorAll('[data-submenu-toggle]');
		submenuToggles.forEach(toggle => {
			toggle.addEventListener('click', () => {
				setTimeout(() => {
					const anySubmenuOpen = document.querySelector('[data-mobile-submenu]:not(.max-md\\:translate-x-full)');
					header.setAttribute('data-dropdown-active', anySubmenuOpen ? 'true' : 'false');
				}, 50);
			});
		});
	}

	initHeaderTheme() {
		const header = document.querySelector('header');
		if (!header) return;

		// Remove scrolled attribute on page load/transition
		header.removeAttribute('data-header-scrolled');

		// Clean up existing scroll triggers
		ScrollTrigger.getAll().forEach(trigger => {
			if (trigger.vars.id && trigger.vars.id.startsWith('headerTheme')) {
				trigger.kill();
			}
		});

		// Get first section to determine initial theme
		const firstSection = document.querySelector('section');

		if (!firstSection) {
			header.setAttribute('data-header-theme', 'light');
			return;
		}

		// Set initial theme based on first/hero section (default to dark if not set)
		const firstTheme = firstSection.getAttribute('data-section-theme') || 'dark';
		const initialTheme = firstTheme === 'dark' ? 'light' : 'dark';
		header.setAttribute('data-header-theme', initialTheme);
	}
	
	headerAnimateIn() {
		const header = document.querySelector('header');
		if (!header) return;
		
		if (header.classList.contains('opacity-0')) {
			setTimeout( () => {
				header.classList.remove('opacity-0')
				header.classList.add('opacity-100')
			}, 1000)
		}
	}
}